import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { registerUser, checkUsernameAvailability, checkEmailAvailability } from "../../services/api/userService";
import { PASSWORD_RULE, passwordProblem } from "../../utils/passwordPolicy";
import { apiClient } from "../../services/api/apiService";
import "../../styles/features/auth.css";

// Never a second account for the same email: point to sign-in / password reset.
const EMAIL_TAKEN = "You already have a Sportifide account with this email.";

function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", username: "" });
  const [message, setMessage] = useState("");
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  // When an email is taken but account is unverified, store it here to offer resend
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameValid, setUsernameValid] = useState(false);
  // When a username is taken by an unverified account, store it here
  const [unverifiedUsername, setUnverifiedUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    
    // Clear validation when typing
    if (e.target.name === "email") {
      setEmailError("");
      setEmailValid(false);
      setUnverifiedEmail("");
    } else if (e.target.name === "username") {
      setUsernameError("");
      setUsernameValid(false);      setUnverifiedUsername("");    }
  };

  // Returns true when the email is available and valid, false otherwise.
  // This return value is used by handleSubmit to get fresh data when state is stale.
  const checkEmail = async (email) => {
    if (!email) {
      setEmailError("Email is required");
      setEmailValid(false);
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Invalid email format");
      setEmailValid(false);
      return false;
    }

    setEmailChecking(true);
    try {
      const response = await checkEmailAvailability(email);

      // Real backend shape: { success, message: { available, email, is_email_verified? }, data: "string" }
      // Fall back through alternate shapes for safety (data-wrapped or flat).
      const available =
        response?.data?.message?.available !== undefined ? response.data.message.available :
        response?.data?.available         !== undefined ? response.data.available :
        response?.message?.available      !== undefined ? response.message.available :
        response?.available;

      const isEmailVerified =
        response?.data?.message?.is_email_verified !== undefined ? response.data.message.is_email_verified :
        response?.data?.is_email_verified           !== undefined ? response.data.is_email_verified :
        response?.message?.is_email_verified        !== undefined ? response.message.is_email_verified :
        undefined;

      if (available === true) {
        setEmailError("");
        setEmailValid(true);
        setUnverifiedEmail("");
        return true;
      } else if (isEmailVerified === false) {
        // Account exists but was never activated — offer to resend instead of blocking
        setUnverifiedEmail(email);
        setEmailError("");
        setEmailValid(false);
        return false;
      } else {
        setEmailError(EMAIL_TAKEN);
        setEmailValid(false);
        setUnverifiedEmail("");
        return false;
      }
    } catch (error) {
      console.error("Error checking email:", error);
      setEmailError("Error checking email availability");
      setEmailValid(false);
      return false;
    } finally {
      setEmailChecking(false);
    }
  };

  const checkUsername = async (username) => {
    if (!username || username.length < 3) {
      setUsernameError("Username must be at least 3 characters");
      setUsernameValid(false);
      return;
    }

    // Validate format
    const usernameRegex = /^[a-z0-9_]{3,50}$/;
    if (!usernameRegex.test(username)) {
      setUsernameError("Only lowercase letters, numbers, and underscore allowed");
      setUsernameValid(false);
      return;
    }

    setUsernameChecking(true);
    try {
      const response = await checkUsernameAvailability(username);

      const available =
        response?.data?.message?.available !== undefined ? response?.data?.message?.available :
        response?.data?.available !== undefined ? response?.data?.available :
        response?.message?.available !== undefined ? response?.message?.available :
        response?.available;

      const isUsernameVerified =
        response?.data?.message?.is_email_verified !== undefined ? response.data.message.is_email_verified :
        response?.data?.is_email_verified           !== undefined ? response.data.is_email_verified :
        response?.message?.is_email_verified        !== undefined ? response.message.is_email_verified :
        undefined;

      if (available === true) {
        setUsernameError("");
        setUsernameValid(true);
        setUnverifiedUsername("");
      } else if (isUsernameVerified === false) {
        // Username held by a stuck unverified account — offer to activate it
        setUnverifiedUsername(username);
        setUsernameError("");
        setUsernameValid(false);
      } else {
        setUsernameError("Username already taken");
        setUsernameValid(false);
        setUnverifiedUsername("");
      }
    } catch (error) {
      console.error("Error checking username:", error);
      setUsernameError("Error checking username");
      setUsernameValid(false);
    } finally {
      setUsernameChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If email validity is stale (e.g. user deleted their DB row after the last blur-check),
    // run a fresh check now so they aren't permanently blocked by a cached result.
    let currentEmailValid = emailValid;
    if (!currentEmailValid && !emailChecking) {
      currentEmailValid = await checkEmail(form.email);
    }

    if (!currentEmailValid) {
      setMessage("Please enter a valid and available email");
      return;
    }

    if (!usernameValid) {
      setMessage("Please enter a valid and available username");
      return;
    }

    const passwordError = passwordProblem(form.password);
    if (passwordError) {
      setMessage(passwordError);
      return;
    }
    
    try {
      await registerUser(form);
      setRegisteredEmail(form.email);
      setRegistered(true);
    } catch (err) {
      // Account exists but unverified — backend resent the link; show CheckEmailScreen
      if (err.response?.data?.code === "ACCOUNT_PENDING_VERIFICATION") {
        setRegisteredEmail(err.response.data.email || form.email);
        setRegistered(true);
        return;
      }
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
      const details = err.response?.data?.details || "";
      setMessage("Registration failed: " + errorMsg + (details ? " " + details : ""));
    }
  };

  if (registered) {
    return <CheckEmailScreen email={registeredEmail} />;
  }

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>Register</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="reg-name">Full Name</label>
            <input
              id="reg-name"
              name="name"
              type="text"
              autoComplete="name"
              placeholder="Full Name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="reg-username">Username</label>
            <input
              id="reg-username"
              name="username"
              type="text"
              autoComplete="username"
              placeholder="Username"
              value={form.username}
              onChange={handleChange}
              onBlur={(e) => checkUsername(e.target.value)}
              className={usernameError ? 'input-error' : usernameValid ? 'input-success' : ''}
              pattern="[a-z0-9_]{3,50}"
              title="3-50 characters: lowercase letters, numbers, underscore only"
              required
            />
            <small className="field-hint">Lowercase letters, numbers and underscore only</small>
            {usernameChecking && (
              <small className="field-hint checking">Checking availability...</small>
            )}
            {usernameError && (
              <small className="field-error">{usernameError}</small>
            )}
            {usernameValid && (
              <small className="field-success">✓ Username available</small>
            )}
            {unverifiedUsername && (
              <div className="field-unverified-notice">
                <span>⚠️ This username belongs to an inactive account.</span>
                <UsernameClaimButton username={unverifiedUsername} />
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="reg-email">Email</label>
            <input
              id="reg-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              onBlur={(e) => checkEmail(e.target.value)}
              className={emailError ? 'input-error' : emailValid ? 'input-success' : ''}
              required
            />
            {emailChecking && (
              <small className="field-hint checking">Checking availability...</small>
            )}
            {emailError && (
              <small className="field-error">
                {emailError}
                {emailError === EMAIL_TAKEN && (
                  <>
                    {" "}<Link to="/login">Sign in</Link> — if you forgot your password, use{" "}
                    <Link to="/forgot-password">Forgot password</Link>.
                  </>
                )}
              </small>
            )}
            {emailValid && (
              <small className="field-success">✓ Email available</small>
            )}
            {unverifiedEmail && (
              <div className="field-unverified-notice">
                <span>⚠️ This account isn't activated yet.</span>
                <ResendInlineButton email={unverifiedEmail} />
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="reg-password">Password</label>
            <div className="password-wrapper">
              <input
                id="reg-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                aria-describedby="reg-password-rule"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            <small id="reg-password-rule" style={{ display: "block", marginTop: "0.35rem", fontSize: "0.78rem", opacity: 0.75 }}>{PASSWORD_RULE}</small>
          </div>
          <button type="submit" className="auth-button">
            Register
          </button>
        </form>

        {message && (
          <div
            className={
              message.includes("successful")
                ? "success-message"
                : "error-message"
            }
          >
            {message}
          </div>
        )}

        <div className="auth-links">
          <Link to="/login">Already have an account? Login</Link>
        </div>
      </div>
    </div>
  );
}

export default Register;

/**
 * Shown when a username is held by an unverified account.
 * Sends an activation link to the email on that account so the owner can reclaim it.
 */
function UsernameClaimButton({ username }) {
  const [status, setStatus] = useState("");
  const handleClaim = async () => {
    setStatus("sending");
    try {
      await apiClient.post("/auth/resend-verification", { username });
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };
  return (
    <button
      type="button"
      className="auth-button"
      style={{ marginTop: "0.5rem", padding: "0.4rem 0.9rem", fontSize: "0.85rem" }}
      onClick={handleClaim}
      disabled={status === "sending" || status === "sent"}
    >
      {status === "sending" && "Sending…"}
      {status === "sent" && "✅ Activation link sent to the account's email!"}
      {status === "error" && "Failed — try again"}
      {!status && "Send activation link to unlock username →"}
    </button>
  );
}

/**
 * Small inline button shown in the email field when the entered email belongs
 * to an unverified account. Lets the user request a new activation link
 * without leaving the register page.
 */
function ResendInlineButton({ email }) {
  const [status, setStatus] = useState("");
  const handleResend = async () => {
    setStatus("sending");
    try {
      await apiClient.post("/auth/resend-verification", { email });
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };
  return (
    <button
      type="button"
      className="auth-button"
      style={{ marginTop: "0.5rem", padding: "0.4rem 0.9rem", fontSize: "0.85rem" }}
      onClick={handleResend}
      disabled={status === "sending" || status === "sent"}
    >
      {status === "sending" && "Sending…"}
      {status === "sent" && "✅ Activation link sent! Check your inbox."}
      {status === "error" && "Failed — try again"}
      {!status && "Resend activation link →"}
    </button>
  );
}

const RESEND_COOLDOWN = 30;

function CheckEmailScreen({ email }) {
  const [sentOnce, setSentOnce] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resendMsg, setResendMsg] = useState("");
  const [resendSending, setResendSending] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) {
      clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  const handleSend = async () => {
    setResendSending(true);
    setResendMsg("");
    try {
      await apiClient.post("/auth/resend-verification", { email });
      const isFirstSend = !sentOnce;
      setSentOnce(true);
      setCooldown(RESEND_COOLDOWN);
      setResendMsg(
        isFirstSend
          ? "✅ Activation link sent! Check your inbox and spam folder."
          : "✅ Activation link resent! Check your inbox and spam folder."
      );
    } catch (err) {
      setResendMsg(err.response?.data?.message || "Failed to send. Please try again.");
    } finally {
      setResendSending(false);
    }
  };

  const buttonLabel = resendSending
    ? "Sending…"
    : cooldown > 0
    ? `Resend in ${cooldown}s`
    : sentOnce
    ? "Resend Activation Link"
    : "Send Activation Link";

  return (
    <div className="auth-container">
      <div className="auth-form" style={{ textAlign: "center" }}>
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📧</div>
        <h2>Activate Your Account</h2>
        <p style={{ color: "#555", marginBottom: "1rem" }}>
          Click below to send an activation link to<br />
          <strong>{email}</strong>
        </p>
        <p style={{ color: "#555", fontSize: "0.9rem", marginBottom: "1.5rem" }}>
          Click the link in the email to activate your account. Check your spam folder if you don't see it.
        </p>

        <button
          onClick={handleSend}
          disabled={resendSending || cooldown > 0}
          className="auth-button"
          style={{
            background: cooldown > 0 ? "#ccc" : undefined,
            cursor: cooldown > 0 ? "not-allowed" : "pointer",
            marginBottom: "0.5rem",
          }}
        >
          {buttonLabel}
        </button>
        {resendMsg && (
          <p style={{
            color: resendMsg.startsWith("✅") ? "#28a745" : "#dc3545",
            fontSize: "0.9rem",
            marginTop: "0.5rem",
          }}>
            {resendMsg}
          </p>
        )}

        <Link
          to="/login"
          className="auth-button"
          style={{ display: "inline-block", textDecoration: "none", marginTop: "1rem", background: "#6c757d" }}
        >
          Go to Login
        </Link>
      </div>
    </div>
  );
}
